import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  useDeleteAccessCode,
  useProject,
  useProjectAccessCodes,
  useRevealAccessCode,
  useSaveAccessCode
} from '../api/queries'
import type { AccessCodeKind, ProjectAccessCode } from '../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDateTime } from '../lib/format'

/** Så længe står en vist kode fremme, før den skjules igen af sig selv. */
const REVEAL_SECONDS = 30

const KINDS: { kind: AccessCodeKind; title: string; add: string; icon: string; placeholder: string }[] = [
  { kind: 'gate', title: 'Porte', add: 'Tilføj port', icon: '🚧', placeholder: 'F.eks. Port ved indkørslen' },
  { kind: 'keybox', title: 'Nøglebokse', add: 'Tilføj nøgleboks', icon: '🔐', placeholder: 'F.eks. Nøgleboks ved hoveddøren' }
]

/**
 * Porte og nøglebokse med koder på sagen (beslutning 088).
 *
 * Koden står aldrig i listen. Den hentes først, når man trykker "Vis kode", står
 * fremme i 30 sekunder og ligger kun i komponentens egen tilstand - aldrig i
 * query-cachen. Serveren skriver hver visning i sagens aktivitetslog.
 */
export function ProjectAccessCodesPage() {
  const { id } = useParams()
  const projectId = Number(id)
  const { data: project } = useProject(projectId)
  const { data: codes, isLoading, error } = useProjectAccessCodes(projectId)
  const [adding, setAdding] = useState<AccessCodeKind | null>(null)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card>
        <Link to={`/projects/${projectId}`} className="small muted">
          ← Tilbage til sagen
        </Link>
        <h2 style={{ fontSize: 22, marginTop: 6 }}>🔑 Port og nøgleboks</h2>
        {project && <div className="small muted">{project.project.name}</div>}
        <p className="tiny muted" style={{ marginTop: 8 }}>
          Koderne er krypteret på serveren og vises først, når du trykker "Vis kode". Hver gang en kode
          bliver vist, står det i sagens aktivitetslog, hvem der så den.
        </p>
        <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          {KINDS.map((k) => (
            <button key={k.kind} className="btn btn--primary btn--sm" onClick={() => setAdding(k.kind)}>
              + {k.add}
            </button>
          ))}
        </div>
      </Card>

      {adding && (
        <AccessCodeForm projectId={projectId} kind={adding} onDone={() => setAdding(null)} />
      )}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {codes &&
        KINDS.map((k) => {
          const items = codes.filter((c) => c.kind === k.kind)
          return (
            <Card key={k.kind} title={`${k.icon} ${k.title} (${items.length})`}>
              {items.length === 0 ? (
                <EmptyState>
                  {k.kind === 'gate' ? 'Der er ingen porte på sagen.' : 'Der er ingen nøglebokse på sagen.'}
                </EmptyState>
              ) : (
                <div className="stack">
                  {items.map((item) => (
                    <AccessCodeRow key={item.id} projectId={projectId} item={item} />
                  ))}
                </div>
              )}
            </Card>
          )
        })}
    </div>
  )
}

function AccessCodeRow({ projectId, item }: { projectId: number; item: ProjectAccessCode }) {
  const reveal = useRevealAccessCode(projectId)
  const remove = useDeleteAccessCode(projectId)
  const [code, setCode] = useState<string | null>(null)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Koden skjules igen af sig selv - telefonen kan ligge fremme på et bord.
  useEffect(() => {
    if (code === null) return
    if (secondsLeft <= 0) {
      setCode(null)
      return
    }
    const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [code, secondsLeft])

  async function show() {
    setError(null)
    try {
      const result = await reveal.mutateAsync(item.id)
      setCode(result.code)
      setSecondsLeft(REVEAL_SECONDS)
      // Svaret må ikke blive liggende i mutationens tilstand.
      reveal.reset()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Koden kunne ikke vises.')
    }
  }

  async function del() {
    if (!window.confirm(`Slet ${item.kindLabel.toLowerCase()}en "${item.name}"? Koden kan ikke hentes igen.`)) return
    setError(null)
    try {
      await remove.mutateAsync(item.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke slette.')
    }
  }

  if (editing) {
    return <AccessCodeForm projectId={projectId} kind={item.kind} item={item} onDone={() => setEditing(false)} />
  }

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--border)' }}>
      <div className="row row--between" style={{ alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>{item.name}</div>
          {item.note && <div className="small muted">{item.note}</div>}
          <div className="tiny muted">
            {item.updatedByName ? `Senest rettet af ${item.updatedByName} · ` : ''}
            {formatDateTime(item.updatedAt)}
          </div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn btn--sm" onClick={() => setEditing(true)}>
            Ret
          </button>
          <button className="btn btn--sm" onClick={() => void del()} disabled={remove.isPending}>
            Slet
          </button>
        </div>
      </div>

      <div className="row" style={{ gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        {code === null ? (
          <>
            <span aria-label="Koden er skjult" style={{ fontFamily: 'monospace', fontSize: 20, letterSpacing: 4 }}>
              ••••••
            </span>
            <button className="btn btn--primary btn--sm" onClick={() => void show()} disabled={reveal.isPending}>
              {reveal.isPending ? 'Henter ...' : 'Vis kode'}
            </button>
          </>
        ) : (
          <>
            <span style={{ fontFamily: 'monospace', fontSize: 26, fontWeight: 700, letterSpacing: 3 }}>{code}</span>
            <button className="btn btn--sm" onClick={() => setCode(null)}>
              Skjul
            </button>
            <span className="tiny muted">Skjules om {secondsLeft} sek.</span>
          </>
        )}
      </div>

      {error && (
        <div className="error-box" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}
    </div>
  )
}

function AccessCodeForm({
  projectId,
  kind: initialKind,
  item,
  onDone
}: {
  projectId: number
  kind: AccessCodeKind
  item?: ProjectAccessCode
  onDone: () => void
}) {
  const save = useSaveAccessCode(projectId)
  const [kind, setKind] = useState<AccessCodeKind>(initialKind)
  const [name, setName] = useState(item?.name ?? '')
  const [note, setNote] = useState(item?.note ?? '')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const meta = KINDS.find((k) => k.kind === kind)!

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        id: item?.id,
        body: {
          kind,
          name,
          note: note.trim() ? note : item ? '' : null,
          // Ved en rettelse betyder et tomt felt "behold koden".
          ...(item && !code.trim() ? {} : { code })
        }
      })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme.')
    }
  }

  return (
    <Card title={item ? `Ret ${item.kindLabel.toLowerCase()}` : meta.add}>
      <form onSubmit={submit} autoComplete="off">
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Type</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as AccessCodeKind)}>
              <option value="gate">Port</option>
              <option value="keybox">Nøgleboks</option>
            </select>
          </label>
          <label className="field">
            <span className="field__label">Hvad er det? *</span>
            <input required maxLength={200} placeholder={meta.placeholder} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">{item ? 'Ny kode' : 'Kode *'}</span>
            <input
              required={!item}
              maxLength={100}
              // Ikke type="password": montøren skal kunne se, hvad han taster. Browseren
              // må heller ikke tilbyde at gemme den som en adgangskode.
              autoComplete="off"
              spellCheck={false}
              inputMode="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {item && <span className="tiny muted">Lad feltet stå tomt for at beholde koden.</span>}
          </label>
          <label className="field">
            <span className="field__label">Bemærkning</span>
            <input
              maxLength={500}
              placeholder="F.eks. tryk # efter koden, boksen sidder bag nedløbsrøret"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
        </div>

        {error && (
          <div className="error-box" style={{ marginTop: 10 }}>
            {error}
          </div>
        )}

        <div className="btn-group" style={{ marginTop: 12 }}>
          <button className="btn btn--primary btn--sm" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn btn--sm" type="button" onClick={onDone}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}
